"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { requestPermissionAndGetToken } from "firebase-helper";
import {
  Phone,
  ChevronLeft,
  ChevronRight,
  Scissors,
  Award,
  MapPin,
  ChevronDown,
  Clock,
  ChevronUp,
  Download,
  Share,
  PlusSquare,
  Smartphone,
  X,
  BellRing,
  Calendar,
  ShoppingBag,
  GraduationCap,
  ShieldCheck,
  Sparkles,
  ExternalLink,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Truck,
  RefreshCw,
  Lock,
  FileText,
  Cookie,
  Mail,
  Eye,
  Home,
} from "lucide-react";
import "./landingpage.css";
import { BsInstagram, BsWhatsapp } from "react-icons/bs";

// ─── SERVICE BACK SLIDER ──────────────────────────────────────────────────────
const ServiceBackSlider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!images || images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, 3000);
    return () => clearInterval(interval);
  }, [images]);

  if (!images || images.length === 0) return null;

  return (
    <div className="service-back-slider-bg">
      {images.map((img, index) => (
        <img
          key={index}
          src={img}
          alt="Staff"
          className={`service-bg-img ${index === currentIndex ? "active" : ""}`}
        />
      ))}
      <div className="service-bg-overlay"></div>
    </div>
  );
};

// ─── HASH → PAGE MAP ──────────────────────────────────────────────────────────
const HASH_TO_PAGE = {
  "": { page: "home", legal: null },
  "#": { page: "home", legal: null },
  "#/": { page: "home", legal: null },
  "#/ana-sayfa": { page: "home", legal: null },
  "#/hizmetler": { page: "services", legal: null },
  "#/paketler": { page: "services", legal: null },
  "#/magaza": { page: "store", legal: null },
  "#/akademi": { page: "academy", legal: null },
  "#/egitim": { page: "academy", legal: null },
  "#/galeri": { page: "gallery", legal: null },
  "#/kurumsal": { page: "about", legal: null },
  "#/hakkimizda": { page: "about", legal: null },
  "#/iletisim": { page: "contact", legal: null },
  "#/yasal": { page: "legal", legal: "kvkk" },
  "#/kvkk": { page: "legal", legal: "kvkk" },
  "#/mesafeli-satis-sozlesmesi": { page: "legal", legal: "mesafeli-satis" },
  "#/on-bilgilendirme-formu": { page: "legal", legal: "on-bilgilendirme" },
  "#/iptal-ve-iade": { page: "legal", legal: "iptal-iade" },
  "#/gizlilik-ve-cerez": { page: "legal", legal: "cerez-politikasi" },
  "#/kullanim-kosullari": { page: "legal", legal: "kullanim-kosullari" },
};

const PAGE_TO_HASH = {
  home: "#/",
  services: "#/hizmetler",
  store: "#/magaza",
  academy: "#/akademi",
  gallery: "#/galeri",
  about: "#/kurumsal",
  contact: "#/iletisim",
  legal: "#/kvkk",
};

// ─── ANA COMPONENT ────────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Multi-Page Navigation State
  const [activePage, setActivePage] = useState("home");
  const [legalTab, setLegalTab] = useState("kvkk");
  const [navOpen, setNavOpen] = useState(false);
  const [navbarScrolled, setNavbarScrolled] = useState(false);

  // Sliders & UI State
  const servicesSliderRef = useRef(null);
  const circleRef = useRef(null);
  const [flippedService, setFlippedService] = useState(null);
  const [serviceFilter, setServiceFilter] = useState("all");
  const [galleryFilter, setGalleryFilter] = useState("all");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [currentMobileImageIndex, setCurrentMobileImageIndex] = useState(0);
  const showScrollTop = true;
  const [installModalVisible, setInstallModalVisible] = useState(false);

  // Cookie Consent State
  const [cookieBannerVisible, setCookieBannerVisible] = useState(false);
  const [cookieModalVisible, setCookieModalVisible] = useState(false);
  const [cookiePrefs, setCookiePrefs] = useState({
    necessary: true,
    analytics: true,
    functional: true,
    marketing: false,
  });

  const businessInfo = {
    name: "Ahmet Tosun",
    fullName: "Ahmet Tosun Luxury",
    legalTitle: "Ahmet Tosun Luxury Hair Design & Grooming Studio",
    slogan: "Where Tradition Meets Modern Style",
    phone: "0545 926 50 66",
    whatsapp: "905459265066",
    instagram: "ahmet.tosun.luxury",
    email: "info@ahmettosunluxury.com",
    address: "Kınıklı, 6037. Sk. NO:3 İÇ KAPI NO:10, 20160 Pamukkale/Denizli",
    workingHours: "Pazartesi - Cumartesi : 09:00 - 21:00",
    storeUrl: "https://magaza.ahmettosunluxury.com",
    storeDomain: "magaza.ahmettosunluxury.com",
    academyUrl: "https://egitim.ahmettosunluxury.com",
    academyDomain: "egitim.ahmettosunluxury.com",
  };

  const desktopHeroImage = "/images/img_9.webp";
  const mobileHeroImages = [
    "/images/img_9.webp",
    "/images/img_2.jpeg",
    "/images/img_5.jpeg",
    "/images/img_6.jpeg",
  ];

  // ─── URL HASH ROUTING & COOKIE CHECK ──────────────────────────────────────
  useEffect(() => {
    const syncFromHash = () => {
      const currentHash = window.location.hash.toLowerCase();
      const matched = HASH_TO_PAGE[currentHash];
      if (matched) {
        setActivePage(matched.page);
        if (matched.legal) setLegalTab(matched.legal);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, []);

  useEffect(() => {
    try {
      const savedConsent = localStorage.getItem("atl_cookie_consent_v1");
      if (!savedConsent) {
        setCookieBannerVisible(true);
      } else {
        const parsed = JSON.parse(savedConsent);
        if (parsed && typeof parsed === "object") {
          setCookiePrefs((prev) => ({ ...prev, ...parsed, necessary: true }));
        }
      }
    } catch (e) {
      setCookieBannerVisible(true);
    }
  }, []);

  const navigateToPage = (pageId, customLegalTab = null) => {
    setActivePage(pageId);
    if (customLegalTab) {
      setLegalTab(customLegalTab);
      const legalHashMap = {
        kvkk: "#/kvkk",
        "mesafeli-satis": "#/mesafeli-satis-sozlesmesi",
        "on-bilgilendirme": "#/on-bilgilendirme-formu",
        "iptal-iade": "#/iptal-ve-iade",
        "cerez-politikasi": "#/gizlilik-ve-cerez",
        "kullanim-kosullari": "#/kullanim-kosullari",
      };
      window.history.pushState(
        null,
        "",
        legalHashMap[customLegalTab] || "#/kvkk"
      );
    } else {
      window.history.pushState(null, "", PAGE_TO_HASH[pageId] || "#/");
    }
    setNavOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ─── COOKIE HANDLERS ──────────────────────────────────────────────────────
  const saveCookieConsent = (prefsToSave) => {
    const finalPrefs = {
      ...prefsToSave,
      necessary: true,
      updatedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem("atl_cookie_consent_v1", JSON.stringify(finalPrefs));
    } catch (e) {
      console.error(e);
    }
    setCookiePrefs(finalPrefs);
    setCookieBannerVisible(false);
    setCookieModalVisible(false);
  };

  const handleAcceptAllCookies = () => {
    saveCookieConsent({
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
    });
  };

  const handleAcceptNecessaryCookies = () => {
    saveCookieConsent({
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
    });
  };

  // ─── BİLDİRİM İZNİ ────────────────────────────────────────────────────────
  const handleEnableNotifications = async () => {
    if (!("Notification" in window)) {
      alert("Bu tarayıcı bildirimleri desteklemiyor.");
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        const token = await requestPermissionAndGetToken();
        if (token) {
          if (user) {
            console.log("Kullanıcı giriş yapmış, token alındı.");
          } else {
            localStorage.setItem("pending_fcm_token", token);
            console.log(
              "Kullanıcı giriş yapmadığı için token localStorage'a park edildi."
            );
          }
          alert("Bildirim izni başarıyla alındı! Teşekkürler.");
        }
      } else if (permission === "denied") {
        alert(
          "Bildirim izni reddedildi. Cihaz ayarlarından bildirimleri açabilirsiniz."
        );
      }
    } catch (error) {
      console.error("Bildirim izni hatası:", error);
      alert("Bir hata oluştu. Lütfen tekrar deneyin.");
    }
  };

  // ─── HİZMETLER ────────────────────────────────────────────────────────────
  const services = [
    {
      id: 1,
      category: "sac-sakal",
      icon: <Scissors size={38} />,
      title: "Saç Kesimi & Yıkama",
      description: "Anatomik yüz yapısına özel profesyonel kesim ve bakım.",
      details:
        "İlk kez gelenlere özel: Saçları daha canlı ve ipeksi tutacak Saç Maskesi + Cildi parlak gösterecek Yüz Maskesi HEDİYE.",
      duration: "45 dk",
      staffImages: ["/images/img_clsn_2.jpeg"],
    },
    {
      id: 2,
      category: "sac-sakal",
      icon: <Award size={38} />,
      title: "Sakal Tasarımı",
      description: "Yüz hatlarınıza uygun estetik sakal şekillendirme.",
      details:
        "İlk kez gelenlere özel: Cildi daha parlak ve yumuşak gösterecek Yüz Maskesi HEDİYE.",
      duration: "25 dk",
      staffImages: ["/images/img_clsn_1.jpeg"],
    },
    {
      id: 3,
      category: "sac-sakal",
      icon: <Scissors size={38} />,
      title: "Saç & Sakal Kesimi",
      description: "Tam kapsamlı premium değişim paketi.",
      details:
        "İlk kez gelenlere özel: Saç Maskesi + Yüz Maskesi Bakımı + Tonik Yüz Temizleme HEDİYE.",
      duration: "60 dk",
      staffImages: ["/images/img_clsn_3.jpeg"],
    },
    {
      id: 4,
      category: "sac-sakal",
      icon: <Award size={38} />,
      title: "Junior Kesim",
      description: "Minik misafirlerimiz için özenli saç tasarımı.",
      details:
        "İlk kez gelen minik misafirlerimize özel: Yıkama + Şekillendirme HEDİYE.",
      duration: "40 dk",
      staffImages: ["/images/img_clsn_2.jpeg"],
    },
    {
      id: 5,
      category: "cilt-bakim",
      icon: <Scissors size={38} />,
      title: "Altın Oran Kaş Tasarımı",
      description: "İfadeyi güçlendiren profesyonel dokunuşlar.",
      details:
        "İlk kez gelenlere özel: Kaş alımı sonrası yatıştırıcı bakım HEDİYE.",
      duration: "20 dk",
      staffImages: ["/images/img_clsn_4.jpeg"],
    },
    {
      id: 6,
      category: "cilt-bakim",
      icon: <Award size={38} />,
      title: "Cilt Bakımı",
      description: "Derinlemesine temizlik ve yenilenme.",
      details:
        "İlk kez gelenlere özel: Cilt bakımı sonrası yatıştırıcı nemlendirici maske HEDİYE.",
      duration: "35 dk",
      staffImages: ["/images/img_clsn_4.jpeg"],
    },
    {
      id: 7,
      category: "cilt-bakim",
      icon: <Scissors size={38} />,
      title: "Manikür & Pedikür",
      description: "El ve ayak bakımında hijyenik ve estetik çözüm.",
      details: "İlk defa gelenlere özel: +5 dk El + Ayak Masajı HEDİYE.",
      duration: "40 dk",
      staffImages: ["/images/img_clsn_4.jpeg"],
    },
    {
      id: 8,
      category: "renklendirme",
      icon: <Award size={38} />,
      title: "Renklendirme",
      description: "Doğal görünümlü profesyonel boyama işlemleri.",
      details:
        "İlk kez gelenlere özel: Renk koruyucu şampuanla yıkama + Tonik ve After Shave HEDİYE.",
      duration: "120 dk",
      staffImages: ["/images/img_clsn_3.jpeg"],
    },
  ];

  // ─── MAĞAZA KOLEKSİYONLARI (magaza.ahmettosunluxury.com) ──────────────────
  const storeCollections = [
    {
      id: "sc1",
      tag: "EN ÇOK TERCİH EDİLEN",
      title: "Premium Saç Şekillendirici & Wax Serisi",
      desc: "Gün boyu esnek veya ekstra güçlü tutuş sağlayan, saçta kalıntı bırakmayan profesyonel mat kil, pomad ve hacim pudrası koleksiyonu.",
      image: "/images/img_9.webp",
    },
    {
      id: "sc2",
      tag: "SAKAL BAKIMI",
      title: "Soğuk Sıkım Sakal Bakım Yağları & Serumlar",
      desc: "Sakal köklerini besleyen, kaşıntıyı önleyen ve doğal parlaklık kazandıran argan, jojoba ve tatlı badem yağı bazlı iksirler.",
      image: "/images/img_clsn_1.jpeg",
    },
    {
      id: "sc3",
      tag: "ONARICI BAKIM",
      title: "Keratin Kompleks Şampuan & Saç Maskeleri",
      desc: "Tuzsuz ve sülfatsız özel formülüyle saç derisini koruyan, işlem görmüş veya yıpranmış saçları derinlemesine onaran bakım ürünleri.",
      image: "/images/img_clsn_2.jpeg",
    },
    {
      id: "sc4",
      tag: "CİLT & FERAHLIK",
      title: "Cilt Bakım, Tonik & Luxury After-Shave",
      desc: "Tıraş sonrası cildi yatıştıran, gözenekleri sıkılaştıran profesyonel yüz tonikleri, soyulabilir maskeler ve imza kolonya serisi.",
      image: "/images/img_clsn_4.jpeg",
    },
    {
      id: "sc5",
      tag: "ÖZEL KUTU",
      title: "VIP Ev Bakım Setleri & Hediye Kitleri",
      desc: "Ahmet Tosun Luxury salon deneyimini evinize taşıyan, birbiriyle uyumlu ürünlerden oluşturulmuş tam kapsamlı kombin bakım paketleri.",
      image: "/images/img_6.jpeg",
    },
    {
      id: "sc6",
      tag: "PROFESYONEL EKİPMAN",
      title: "Salon Tipi Tarak, Fırça & Aksesuarlar",
      desc: "Antistatik karbon taraklar, doğal kıl sakal fırçaları ve profesyonel şekillendirme yardımcı ekipmanları.",
      image: "/images/img_5.jpeg",
    },
  ];

  // ─── AKADEMİ PROGRAMLARI (egitim.ahmettosunluxury.com) ────────────────────
  const academyPrograms = [
    {
      id: "ap1",
      tag: "MASTERCLASS • SEVİYE 1-3",
      title: "Anatomik Saç Kesimi & Modern Fade Matematiği",
      desc: "Kafa anatomisi okuma, kusursuz sıfırlama (skin fade), makas-tarak koordinasyonu ve kişiye özel geometrik kesim teknikleri.",
      image: "/images/img_clsn_2.jpeg",
      highlights: [
        "Kafa ve Yüz Morfolojisi Analizi",
        "Kusursuz Fade & Geçiş Teknikleri",
        "Dokulandırma (Texturizing) ve Stil Verme",
      ],
    },
    {
      id: "ap2",
      tag: "UZMANLIK EĞİTİMİ",
      title: "Altın Oran Sakal & Kaş Tasarımı Eğitimi",
      desc: "Erkek yüz ifadesini baştan yaratan altın oran sakal hatları, sıcak havlu ritüeli, ustura açıları ve doğal kaş tasarımı.",
      image: "/images/img_clsn_1.jpeg",
      highlights: [
        "Yüz Tiplerine Göre Sakal Geometrisi",
        "Sıcak Havlu & Geleneksel Ustura Sanatı",
        "Altın Oran Kaş Alımı ve İfade Yönetimi",
      ],
    },
    {
      id: "ap3",
      tag: "İLERİ SEVİYE TEKNİK",
      title: "Renklendirme, Gri Kapatma & Kimyasal İşlemler",
      desc: "Erkek saç ve sakalında doğal tonlama, beyaz kapama, platin/gümüş açma işlemleri ve işlem sonrası keratin protokolleri.",
      image: "/images/img_clsn_3.jpeg",
      highlights: [
        "Doğal Görünümlü Gri/Beyaz Kapatma",
        "Saç Sağlığını Koruyarak Açma & Tonlama",
        "Keratin Bakım ve Kalıcı Şekillendirme",
      ],
    },
    {
      id: "ap4",
      tag: "İŞLETME & MARKALAŞMA",
      title: "VIP Salon Yönetimi & Luxury Marka Konumlandırma",
      desc: "Standart bir kuaför salonunu yüksek gelirli bir 'Luxury Grooming Studio' markasına dönüştürmenin A'dan Z'ye stratejileri.",
      image: "/images/img_6.jpeg",
      highlights: [
        "VIP Müşteri Deneyimi ve Fiyatlandırma",
        "Dijital Randevu & CRM Yönetimi",
        "Sosyal Medya, Fotoğrafçılık ve İmaj",
      ],
    },
  ];

  // ─── GALERİ GÖRSELLERİ ────────────────────────────────────────────────────
  const galleryImages = [
    {
      id: 1,
      url: "/images/img_9.webp",
      alt: "Anatomik Saç & Sakal Tasarımı",
      category: "tasarim",
    },
    {
      id: 2,
      url: "/images/img_2.jpeg",
      alt: "Modern Fade & Stil Uygulaması",
      category: "tasarim",
    },
    {
      id: 3,
      url: "/images/img_5.jpeg",
      alt: "Ahmet Tosun Luxury İmza Kesim",
      category: "tasarim",
    },
    {
      id: 4,
      url: "/images/img_8.webp",
      alt: "Detaylı Sakal & Kontür Sanatı",
      category: "tasarim",
    },
    {
      id: 5,
      url: "/images/img_1.jpeg",
      alt: "Premium Stil & Bakım",
      category: "tasarim",
    },
    {
      id: 6,
      url: "/images/img_6.jpeg",
      alt: "Ahmet Tosun Luxury VIP Salon Atmosferi",
      category: "salon",
    },
    {
      id: 7,
      url: "/images/img_3.jpeg",
      alt: "Kişiye Özel Saç Tasarımı",
      category: "tasarim",
    },
    {
      id: 8,
      url: "/images/img_4.jpeg",
      alt: "VIP Bakım & Stil Deneyimi",
      category: "salon",
    },
    {
      id: 9,
      url: "/images/img_7.webp",
      alt: "Modern Erkek Saç Trendleri",
      category: "tasarim",
    },
    {
      id: 10,
      url: "/images/img_clsn_1.jpeg",
      alt: "Uzman Sakal & Cilt Uygulaması",
      category: "ekip",
    },
    {
      id: 11,
      url: "/images/img_clsn_2.jpeg",
      alt: "Profesyonel Saç Kesim Ritüeli",
      category: "ekip",
    },
    {
      id: 12,
      url: "/images/img_clsn_3.jpeg",
      alt: "Tam Kapsamlı VIP Değişim",
      category: "ekip",
    },
    {
      id: 13,
      url: "/images/img_clsn_4.jpeg",
      alt: "Cilt Bakımı & Altın Oran Kaş Tasarımı",
      category: "ekip",
    },
  ];

  // ─── SIKÇA SORULAN SORULAR ────────────────────────────────────────────────
  const faqItems = [
    {
      q: "Nasıl online randevu alabilirim?",
      a: "Sitemizdeki 'Randevu Al' butonuna tıklayarak saniyeler içinde hesabınıza giriş yapabilir, dilediğiniz hizmet veya VIP paketi, uzmanımızı ve size en uygun gün/saati seçerek online randevunuzu oluşturabilirsiniz.",
    },
    {
      q: "İlk kez gelen misafirlere özel hediye uygulaması nedir?",
      a: "Ahmet Tosun Luxury ailesine ilk kez katılan misafirlerimize, seçtikleri hizmete göre profesyonel Saç Maskesi, Yüz Maskesi, Tonik Yüz Temizleme veya El-Ayak Masajı gibi tamamlayıcı VIP bakımlarımız salonumuzun ikramı olarak ücretsiz uygulanmaktadır.",
    },
    {
      q: "magaza.ahmettosunluxury.com üzerinden yapılan alışverişler güvenli mi?",
      a: "Evet. Resmi e-ticaret mağazamız magaza.ahmettosunluxury.com üzerinden yapacağınız tüm alışverişler 256-Bit SSL sertifikası ve BDDK lisanslı iyzico Korumalı Alışveriş altyapısı ile %100 güvence altındadır. Tüm ürünler salonumuzda bizzat kullandığımız orijinal ürünlerdir.",
    },
    {
      q: "egitim.ahmettosunluxury.com Akademi eğitimlerine kimler katılabilir?",
      a: "Mesleğe yeni adım atanlardan tekniğini 'Luxury Studio' seviyesine taşımak isteyen deneyimli berber ve saç tasarımcılarına kadar herkes katılabilir. Hem online video eğitim modüllerimiz hem de Denizli stüdyomuzda birebir uygulamalı sertifikalı eğitimlerimiz mevcuttur.",
    },
    {
      q: "Randevumu iptal etmek veya saatini değiştirmek istersem ne yapmalıyım?",
      a: "Müşteri panelinizdeki 'Randevularım' sekmesinden veya 0545 926 50 66 numaralı VIP iletişim hattımızdan randevu saatinizden en az 2 saat öncesine kadar ücretsiz değişiklik veya iptal işlemi yapabilirsiniz.",
    },
  ];

  const navLinks = [
    { id: "home", text: "Ana Sayfa" },
    { id: "services", text: "Hizmetler" },
    { id: "store", text: "Mağaza", badge: "STORE" },
    { id: "academy", text: "Eğitim & Akademi", badge: "AKADEMİ" },
    { id: "gallery", text: "Galeri" },
    { id: "about", text: "Kurumsal" },
    { id: "contact", text: "İletişim" },
  ];

  // ─── MOBİL GÖRSEL SLIDER ──────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMobileImageIndex((prevIndex) =>
        prevIndex === mobileHeroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 2800);
    return () => clearInterval(interval);
  }, [mobileHeroImages.length]);

  // ─── SLIDER FONKSİYONLARI ─────────────────────────────────────────────────
  const scrollServices = (direction) => {
    if (servicesSliderRef.current) {
      const scrollAmount =
        window.innerWidth > 768 ? 400 : window.innerWidth * 0.85;
      servicesSliderRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleServiceFlip = (serviceId) => {
    setFlippedService(flippedService === serviceId ? null : serviceId);
  };

  // ─── RANDEVU AL (ANA ODAK) → /auth/sign-in ────────────────────────────────
  const handleBookAppointment = () => {
    navigate("/auth/sign-in");
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ─── SCROLL PROGRESS RING & NAVBAR STATE ──────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setNavbarScrolled(scrollTop > 40);

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollableHeight = documentHeight - windowHeight;

      if (scrollableHeight > 0 && circleRef.current) {
        const scrollPercent = scrollTop / scrollableHeight;
        const circumference = 2 * Math.PI * 26;
        const offset = circumference - scrollPercent * circumference;
        circleRef.current.style.strokeDashoffset = offset;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const filteredServices =
    serviceFilter === "all"
      ? services
      : services.filter((s) => s.category === serviceFilter);

  const filteredGallery =
    galleryFilter === "all"
      ? galleryImages
      : galleryImages.filter((g) => g.category === galleryFilter);

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="landing-page luxury-barber">
      {/* ── ÜST KURUMSAL EKOSİSTEM BARI ── */}
      <div className="lp-topbar">
        <div className="lp-container">
          <div className="lp-topbar-inner">
            <div className="lp-topbar-left">
              <a
                href={`tel:${businessInfo.phone.replace(/\s/g, "")}`}
                className="lp-topbar-item"
              >
                <Phone size={13} />
                <span>{businessInfo.phone}</span>
              </a>
              <span className="lp-topbar-item lp-topbar-hide-mobile">
                <Clock size={13} />
                <span>{businessInfo.workingHours}</span>
              </span>
              <span className="lp-topbar-item lp-topbar-hide-mobile">
                <MapPin size={13} />
                <span>Çamlık / Pamukkale, Denizli</span>
              </span>
            </div>

            <div className="lp-topbar-right">
              <a
                href={businessInfo.storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="lp-topbar-pill gold-pill"
              >
                <ShoppingBag size={12} />
                <span>Online Mağaza</span>
                <ExternalLink size={11} />
              </a>
              <a
                href={businessInfo.academyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="lp-topbar-pill"
              >
                <GraduationCap size={12} />
                <span>Luxury Akademi</span>
                <ExternalLink size={11} />
              </a>
              <button
                type="button"
                className="lp-topbar-pill lp-topbar-hide-mobile"
                onClick={() => setInstallModalVisible(true)}
              >
                <Smartphone size={12} />
                <span>Mobil Uygulama</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── ANA KURUMSAL NAVBAR ── */}
      <nav
        className={`landing-navbar luxury-navbar ${
          navbarScrolled ? "navbar-scrolled" : ""
        }`}
      >
        <div className="lp-container">
          <div className="lp-navbar-inner">
            {/* Marka Logosu */}
            <button
              type="button"
              onClick={() => navigateToPage("home")}
              className="lp-brand-link"
              aria-label="Ahmet Tosun Luxury Ana Sayfa"
            >
              <div className="brand-logo">
                <img
                  src="/images/logo_wide.png"
                  alt="Ahmet Tosun Luxury"
                  className="logo-image"
                />
              </div>
            </button>

            {/* Masaüstü Çok Sayfalı Menü */}
            <div className="lp-nav-desktop">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  className={`nav-link${
                    activePage === link.id ? " active" : ""
                  }`}
                  onClick={() => navigateToPage(link.id)}
                >
                  <span>{link.text}</span>
                  {link.badge && (
                    <span className="nav-badge-mini">{link.badge}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Sağ Aksiyon: ANA ODAK RANDEVU AL + Mobil Menü */}
            <div className="lp-navbar-actions">
              <button
                type="button"
                className="btn-nav-book-primary"
                onClick={handleBookAppointment}
              >
                <Calendar size={16} className="btn-icon-inline" />
                <span>Randevu Al</span>
              </button>

              <button
                type="button"
                className="lp-nav-toggler"
                onClick={() => setNavOpen(!navOpen)}
                aria-label="Menüyü Aç/Kapat"
              >
                {navOpen ? (
                  <X size={22} />
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobil Açılır Kurumsal Menü */}
          {navOpen && (
            <div className="lp-nav-mobile-menu">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  className={`nav-link lp-mobile-nav-link${
                    activePage === link.id ? " active" : ""
                  }`}
                  onClick={() => navigateToPage(link.id)}
                >
                  <span>{link.text}</span>
                  {link.badge ? (
                    <span className="nav-badge-mini">{link.badge}</span>
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </button>
              ))}

              <button
                type="button"
                className={`nav-link lp-mobile-nav-link${
                  activePage === "legal" ? " active" : ""
                }`}
                onClick={() => navigateToPage("legal", "kvkk")}
              >
                <span>KVKK & Yasal Sözleşmeler</span>
                <FileText size={16} />
              </button>

              <div className="lp-mobile-ecosystem-grid">
                <a
                  href={businessInfo.storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lp-mobile-eco-card"
                >
                  <span>E-Ticaret Platformu</span>
                  <strong>
                    Mağazaya Git <ExternalLink size={14} />
                  </strong>
                </a>
                <a
                  href={businessInfo.academyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lp-mobile-eco-card"
                >
                  <span>Eğitim Platformu</span>
                  <strong>
                    Akademiye Git <ExternalLink size={14} />
                  </strong>
                </a>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  marginTop: "1rem",
                }}
              >
                <button
                  type="button"
                  className="btn-pillar-primary"
                  onClick={() => {
                    setNavOpen(false);
                    handleBookAppointment();
                  }}
                >
                  <Calendar size={16} />
                  <span>Hemen Randevu Al</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════════════════════
          SAYFA 1: ANA SAYFA (HOME)
          ═══════════════════════════════════════════════════════════════════════ */}
      {activePage === "home" && (
        <main className="lp-page-view">
          {/* ── HERO SECTION ── */}
          <section id="home" className="hero-section luxury-hero">
            <div className="hero-background-desktop">
              <img
                src={desktopHeroImage}
                alt="Ahmet Tosun Luxury Studio"
                className="hero-image-luxury"
              />
            </div>
            <div className="hero-background-mobile">
              {mobileHeroImages.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Ahmet Tosun Luxury Slide ${index + 1}`}
                  className={`hero-image-mobile ${
                    index === currentMobileImageIndex ? "active" : ""
                  }`}
                />
              ))}
            </div>
            <div className="hero-overlay-luxury"></div>

            <div className="hero-container-luxury lp-container">
              <div className="hero-content-luxury">
                <h1 className="hero-title-luxury">
                  {businessInfo.name}
                  <span className="hero-subtitle-luxury">Luxury</span>
                </h1>
                <p className="hero-slogan">{businessInfo.slogan}</p>

                {/* ANA ODAK: RANDEVU AL CTA GRUBU */}
                <div className="hero-cta-group">
                  <button
                    type="button"
                    className="btn-hero-luxury-primary"
                    onClick={handleBookAppointment}
                  >
                    <Calendar size={20} className="btn-icon-inline" />
                    <span>Randevu Al</span>
                    <ArrowRight size={18} className="btn-icon-inline" />
                  </button>
                </div>

                {/* ALT PLATFORMLAR: ONLINE MAĞAZA & EĞİTİM (ZARİF AÇIKLAMALI BAR) */}
                <div className="hero-sub-platforms">
                  <button
                    type="button"
                    className="hero-sub-link"
                    onClick={() => navigateToPage("store")}
                  >
                    <div className="hero-sub-icon">
                      <ShoppingBag size={16} />
                    </div>
                    <div className="hero-sub-body">
                      <span className="hero-sub-title">
                        <span>Online Mağaza</span>
                        <ArrowRight size={14} className="hero-sub-arrow" />
                      </span>
                      <span className="hero-sub-desc">
                        Profesyonel saç & sakal bakım ürünleri
                      </span>
                    </div>
                  </button>

                  <button
                    type="button"
                    className="hero-sub-link"
                    onClick={() => navigateToPage("academy")}
                  >
                    <div className="hero-sub-icon">
                      <GraduationCap size={16} />
                    </div>
                    <div className="hero-sub-body">
                      <span className="hero-sub-title">
                        <span>Eğitim & Akademi</span>
                        <ArrowRight size={14} className="hero-sub-arrow" />
                      </span>
                      <span className="hero-sub-desc">
                        Sertifikalı berberlik & stil eğitimleri
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* ── AHMET TOSUN LUXURY EKOSİSTEMİ (3 ANA SÜTUN) ── */}
          <section className="section-ecosystem">
            <div className="lp-container">
              <div className="section-header-luxury">
                <span className="section-kicker">KURUMSAL MARKA EKOSİSTEMİ</span>
                <h2 className="section-title-luxury">
                  Ahmet Tosun Luxury Dünyası
                </h2>
                <p className="section-subtitle-luxury">
                  VIP erkek bakım stüdyomuzdan profesyonel bakım ürünleri
                  mağazamıza ve uluslararası standartta berberlik akademimize
                  uzanan bütüncül lüks deneyim.
                </p>
              </div>

              <div className="lp-row">
                {/* Sütun 1: VIP Salon & Randevu (ANA ODAK) */}
                <div className="lp-col-third lp-mb-4">
                  <div className="ecosystem-card featured-pillar">
                    <span className="pillar-badge">
                      <Sparkles size={13} /> ANA ODAK • VIP STÜDYO
                    </span>
                    <div className="pillar-icon-wrap">
                      <Calendar size={32} />
                    </div>
                    <h3 className="pillar-title">VIP Salon & Randevu</h3>
                    <div className="pillar-domain">
                      Denizli / Çamlık Premium Stüdyo
                    </div>
                    <p className="pillar-desc">
                      Anatomik yüz ve kafa yapınıza özel saç-sakal tasarımı,
                      altın oran kaş alımı, medikal cilt bakımı ve özel VIP
                      odalarımızda kişiselleştirilmiş bakım ritüelleri.
                    </p>
                    <ul className="pillar-features">
                      <li>
                        <CheckCircle2 size={16} color="var(--lp-accent)" />
                        <span>7/24 Saniyeler İçinde Online Randevu</span>
                      </li>
                      <li>
                        <CheckCircle2 size={16} color="var(--lp-accent)" />
                        <span>İlk Kez Gelen Misafirlere Özel Bakım Hediyesi</span>
                      </li>
                      <li>
                        <CheckCircle2 size={16} color="var(--lp-accent)" />
                        <span>Üst Düzey Hijyen ve Özel İkram Standartları</span>
                      </li>
                    </ul>
                    <div className="pillar-actions">
                      <button
                        type="button"
                        className="btn-pillar-primary"
                        onClick={handleBookAppointment}
                      >
                        <Calendar size={16} />
                        <span>Hemen Randevu Al</span>
                      </button>
                      <button
                        type="button"
                        className="btn-pillar-outline"
                        onClick={() => navigateToPage("services")}
                      >
                        <span>Hizmetlerimizi İncele</span>
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sütun 2: E-Ticaret Mağazası */}
                <div className="lp-col-third lp-mb-4">
                  <div className="ecosystem-card">
                    <span className="pillar-badge">
                      <ShoppingBag size={13} /> RESMİ E-TİCARET
                    </span>
                    <div className="pillar-icon-wrap">
                      <ShoppingBag size={32} />
                    </div>
                    <h3 className="pillar-title">Luxury Online Mağaza</h3>
                    <div className="pillar-domain">
                      {businessInfo.storeDomain}
                    </div>
                    <p className="pillar-desc">
                      Salonumuzda uyguladığımız profesyonel saç şekillendiriciler,
                      soğuk sıkım sakal serumları, keratin şampuanlar ve VIP ev
                      bakım setleri şimdi tek tıkla kapınızda.
                    </p>
                    <ul className="pillar-features">
                      <li>
                        <CheckCircle2 size={16} color="var(--lp-accent)" />
                        <span>iyzico Güvencesiyle 256-Bit SSL Güvenli Ödeme</span>
                      </li>
                      <li>
                        <CheckCircle2 size={16} color="var(--lp-accent)" />
                        <span>%100 Orijinal Profesyonel Salon Ürünleri</span>
                      </li>
                      <li>
                        <CheckCircle2 size={16} color="var(--lp-accent)" />
                        <span>Türkiye Geneli Hızlı & Sigortalı Kargo</span>
                      </li>
                    </ul>
                    <div className="pillar-actions">
                      <a
                        href={businessInfo.storeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-pillar-primary"
                      >
                        <ShoppingBag size={16} />
                        <span>Mağazaya Git</span>
                        <ExternalLink size={15} />
                      </a>
                      <button
                        type="button"
                        className="btn-pillar-outline"
                        onClick={() => navigateToPage("store")}
                      >
                        <span>Koleksiyonları & Detayları Gör</span>
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sütun 3: Eğitim & Akademi Platformu */}
                <div className="lp-col-third lp-mb-4">
                  <div className="ecosystem-card">
                    <span className="pillar-badge">
                      <GraduationCap size={13} /> PROFESYONEL AKADEMİ
                    </span>
                    <div className="pillar-icon-wrap">
                      <GraduationCap size={32} />
                    </div>
                    <h3 className="pillar-title">Luxury Akademi</h3>
                    <div className="pillar-domain">
                      {businessInfo.academyDomain}
                    </div>
                    <p className="pillar-desc">
                      Modern berberlik sanatını, anatomik kesim matematiğini ve
                      VIP salon yönetimini Ahmet Tosun vizyonuyla öğrenin. Online
                      ve yüz yüze sertifikalı masterclass eğitimler.
                    </p>
                    <ul className="pillar-features">
                      <li>
                        <CheckCircle2 size={16} color="var(--lp-accent)" />
                        <span>Online HD Video & 1-e-1 Uygulamalı Eğitim</span>
                      </li>
                      <li>
                        <CheckCircle2 size={16} color="var(--lp-accent)" />
                        <span>İsme Özel Resmi Ahmet Tosun Luxury Sertifikası</span>
                      </li>
                      <li>
                        <CheckCircle2 size={16} color="var(--lp-accent)" />
                        <span>iyzico Altyapısıyla Güvenli Online Kayıt</span>
                      </li>
                    </ul>
                    <div className="pillar-actions">
                      <a
                        href={businessInfo.academyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-pillar-primary"
                      >
                        <GraduationCap size={16} />
                        <span>Eğitim Platformuna Git</span>
                        <ExternalLink size={15} />
                      </a>
                      <button
                        type="button"
                        className="btn-pillar-outline"
                        onClick={() => navigateToPage("academy")}
                      >
                        <span>Akademi Müfredatını İncele</span>
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── HİZMETLER ÖZET SLIDER ── */}
          <section id="services" className="section-services luxury-services">
            <div className="lp-container-fluid">
              <div className="section-header-luxury">
                <span className="section-kicker">SANAT VE USTALIK</span>
                <h2 className="section-title-luxury">Premium Hizmetler</h2>
                <p className="section-subtitle-luxury">
                  Kartların üzerine tıklayarak ilk kez gelen misafirlerimize
                  özel hediyelerimizi inceleyebilir ve hemen randevu
                  alabilirsiniz.
                </p>
              </div>

              <div className="services-slider-container">
                <button
                  type="button"
                  className="slider-nav-btn slider-prev"
                  onClick={() => scrollServices("left")}
                  aria-label="Önceki"
                >
                  <ChevronLeft size={26} />
                </button>

                <div
                  className="services-slider luxury-services-slider"
                  ref={servicesSliderRef}
                >
                  {services.map((service) => (
                    <div
                      key={service.id}
                      className={`service-flip-card ${
                        flippedService === service.id ? "flipped" : ""
                      }`}
                      onClick={() => handleServiceFlip(service.id)}
                    >
                      <div className="service-flip-card-inner">
                        <div className="service-flip-card-front">
                          <span className="service-duration-tag">
                            <Clock size={12} /> {service.duration}
                          </span>
                          <div className="service-icon-luxury">
                            {service.icon}
                          </div>
                          <h3 className="service-title-luxury">
                            {service.title}
                          </h3>
                          <p className="service-description-luxury">
                            {service.description}
                          </p>
                          <div className="flip-hint">
                            Hediye & Detaylar İçin Tıklayın
                          </div>
                        </div>
                        <div className="service-flip-card-back">
                          <ServiceBackSlider images={service.staffImages} />
                          <div className="service-back-content">
                            <h3 className="service-title-luxury lp-mb-3">
                              {service.title}
                            </h3>
                            <div className="gift-box">
                              <Award size={20} className="gift-icon" />
                              <p className="service-details">
                                {service.details}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="btn-service-book"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBookAppointment();
                              }}
                            >
                              <Calendar size={16} className="btn-icon-inline" />
                              <span>Randevu Al</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="slider-nav-btn slider-next"
                  onClick={() => scrollServices("right")}
                  aria-label="Sonraki"
                >
                  <ChevronRight size={26} />
                </button>
              </div>

              <div style={{ textAlign: "center", marginTop: "2rem" }}>
                <button
                  type="button"
                  className="btn-hero-luxury"
                  onClick={() => navigateToPage("services")}
                >
                  <span>Tüm Hizmetleri Detaylı Sayfada İncele</span>
                  <ArrowRight size={17} />
                </button>
              </div>
            </div>
          </section>

          {/* ── SIKÇA SORULAN SORULAR (SSS) ── */}
          <section className="section-about luxury-about">
            <div className="lp-container">
              <div className="section-header-luxury">
                <span className="section-kicker">MERAK EDİLENLER</span>
                <h2 className="section-title-luxury">Sıkça Sorulan Sorular</h2>
                <p className="section-subtitle-luxury">
                  Randevu sistemi, mağaza alışverişleri ve akademi eğitimlerimiz
                  hakkında sık sorulanlar
                </p>
              </div>

              <div className="lp-faq-list">
                {faqItems.map((item, idx) => {
                  const isOpen = openFaqIndex === idx;
                  return (
                    <div
                      key={idx}
                      className={`lp-faq-item ${isOpen ? "open" : ""}`}
                    >
                      <button
                        type="button"
                        className="lp-faq-question"
                        onClick={() => setOpenFaqIndex(isOpen ? -1 : idx)}
                      >
                        <span>{item.q}</span>
                        {isOpen ? (
                          <ChevronUp size={20} color="var(--lp-accent)" />
                        ) : (
                          <ChevronDown size={20} color="var(--lp-accent)" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="lp-faq-answer">{item.a}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SAYFA 2: HİZMETLER (SERVICES PAGE)
          ═══════════════════════════════════════════════════════════════════════ */}
      {activePage === "services" && (
        <main className="lp-page-view">
          <section className="lp-page-hero">
            <img
              src="/images/img_9.webp"
              alt="Premium Hizmetler"
              className="lp-page-hero-bg"
            />
            <div className="lp-page-hero-overlay"></div>
            <div className="lp-container" style={{ textAlign: "center" }}>
              <div className="lp-breadcrumb">
                <button type="button" onClick={() => navigateToPage("home")}>
                  Ana Sayfa
                </button>
                <span>/</span>
                <span>Hizmetlerimiz</span>
              </div>
              <h1 className="lp-page-hero-title">Premium Hizmetlerimiz</h1>
              <p className="lp-page-hero-subtitle">
                Anatomik yüz analizine dayalı kesim teknikleri, altın oran kaş
                ve sakal tasarımı ve derinlemesine cilt bakımı ritüellerimiz.
              </p>
              <div className="lp-page-hero-actions">
                <button
                  type="button"
                  className="btn-hero-luxury-primary"
                  onClick={handleBookAppointment}
                >
                  <Calendar size={18} />
                  <span>Hemen Randevu Al</span>
                </button>
              </div>
            </div>
          </section>

          <section className="section-services luxury-services">
            <div className="lp-container">
              <div className="lp-filter-tabs">
                <button
                  type="button"
                  className={`lp-filter-btn ${
                    serviceFilter === "all" ? "active" : ""
                  }`}
                  onClick={() => setServiceFilter("all")}
                >
                  Tüm Hizmetler ({services.length})
                </button>
                <button
                  type="button"
                  className={`lp-filter-btn ${
                    serviceFilter === "sac-sakal" ? "active" : ""
                  }`}
                  onClick={() => setServiceFilter("sac-sakal")}
                >
                  Saç & Sakal Tasarımı
                </button>
                <button
                  type="button"
                  className={`lp-filter-btn ${
                    serviceFilter === "cilt-bakim" ? "active" : ""
                  }`}
                  onClick={() => setServiceFilter("cilt-bakim")}
                >
                  Cilt, Kaş & El-Ayak Bakımı
                </button>
                <button
                  type="button"
                  className={`lp-filter-btn ${
                    serviceFilter === "renklendirme" ? "active" : ""
                  }`}
                  onClick={() => setServiceFilter("renklendirme")}
                >
                  Renklendirme & Özel İşlemler
                </button>
              </div>

              <div className="lp-services-grid">
                {filteredServices.map((srv) => (
                  <div className="lp-service-detail-card" key={srv.id}>
                    <div className="lp-service-card-media">
                      <img
                        src={srv.staffImages[0] || "/images/img_9.webp"}
                        alt={srv.title}
                      />
                      <span className="service-duration-tag">
                        <Clock size={12} /> {srv.duration}
                      </span>
                    </div>
                    <div className="lp-service-card-body">
                      <h3
                        className="service-title-luxury"
                        style={{ textAlign: "left", marginBottom: "0.5rem" }}
                      >
                        {srv.title}
                      </h3>
                      <p
                        className="service-description-luxury"
                        style={{ textAlign: "left", marginBottom: "0.75rem" }}
                      >
                        {srv.description}
                      </p>
                      <div className="lp-service-gift-banner">
                        <Award
                          size={22}
                          color="#e5c98f"
                          style={{ flexShrink: 0, marginTop: "2px" }}
                        />
                        <span>{srv.details}</span>
                      </div>
                      <button
                        type="button"
                        className="btn-pillar-primary"
                        style={{ marginTop: "auto" }}
                        onClick={handleBookAppointment}
                      >
                        <Calendar size={16} />
                        <span>Bu Hizmet İçin Randevu Al</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SAYFA 3: MAĞAZA / E-TİCARET (magaza.ahmettosunluxury.com)
          ═══════════════════════════════════════════════════════════════════════ */}
      {activePage === "store" && (
        <main className="lp-page-view">
          <section className="lp-page-hero">
            <img
              src="/images/img_6.jpeg"
              alt="Ahmet Tosun Luxury Store"
              className="lp-page-hero-bg"
            />
            <div className="lp-page-hero-overlay"></div>
            <div className="lp-container" style={{ textAlign: "center" }}>
              <div className="lp-breadcrumb">
                <button type="button" onClick={() => navigateToPage("home")}>
                  Ana Sayfa
                </button>
                <span>/</span>
                <span>Luxury Mağaza (E-Ticaret)</span>
              </div>
              <h1 className="lp-page-hero-title">Ahmet Tosun Luxury Store</h1>
              <p className="lp-page-hero-subtitle">
                Salonumuzda uyguladığımız profesyonel saç şekillendiriciler,
                soğuk sıkım sakal bakım yağları, keratin kompleks şampuanlar ve
                VIP ev bakım kitleri iyzico güvencesiyle kapınızda.
              </p>
              <div className="lp-page-hero-actions">
                <a
                  href={businessInfo.storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-hero-luxury-primary"
                  style={{ textDecoration: "none" }}
                >
                  <ShoppingBag size={18} />
                  <span>Online Mağazaya Git ({businessInfo.storeDomain})</span>
                  <ExternalLink size={16} />
                </a>
                <button
                  type="button"
                  className="btn-hero-luxury"
                  onClick={handleBookAppointment}
                >
                  <Calendar size={18} />
                  <span>Salondan Randevu Al</span>
                </button>
              </div>
            </div>
          </section>

          <section className="section-ecosystem">
            <div className="lp-container">
              <div className="section-header-luxury">
                <span className="section-kicker">ÜRÜN KOLEKSİYONLARIMIZ</span>
                <h2 className="section-title-luxury">
                  Profesyonel Bakım Kategorileri
                </h2>
                <p className="section-subtitle-luxury">
                  Tüm ürünleri incelemek ve online sipariş oluşturmak için resmi
                  mağazamızı ziyaret edin
                </p>
              </div>

              <div className="lp-catalog-grid">
                {storeCollections.map((col) => (
                  <div className="lp-catalog-card" key={col.id}>
                    <div className="lp-catalog-img-wrap">
                      <img src={col.image} alt={col.title} />
                      <span className="lp-catalog-tag">{col.tag}</span>
                    </div>
                    <div className="lp-catalog-content">
                      <h3 className="lp-catalog-title">{col.title}</h3>
                      <p className="lp-catalog-desc">{col.desc}</p>
                      <a
                        href={businessInfo.storeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-pillar-primary"
                      >
                        <ShoppingBag size={15} />
                        <span>Mağazada İncele & Satın Al</span>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mağaza Avantajları */}
              <div className="lp-row lp-mt-5">
                <div className="lp-col-quarter lp-mb-4">
                  <div className="contact-card-luxury">
                    <div className="contact-icon-luxury">
                      <ShieldCheck size={30} />
                    </div>
                    <h4 className="contact-title-luxury">%100 Orijinal Ürün</h4>
                    <p className="info-text-luxury">
                      Salonumuzda bizzat kullandığımız ve onayladığımız
                      profesyonel formüller.
                    </p>
                  </div>
                </div>
                <div className="lp-col-quarter lp-mb-4">
                  <div className="contact-card-luxury">
                    <div className="contact-icon-luxury">
                      <Lock size={30} />
                    </div>
                    <h4 className="contact-title-luxury">iyzico Güvencesi</h4>
                    <p className="info-text-luxury">
                      256-Bit SSL ve BDDK lisanslı iyzico altyapısıyla tüm
                      kartlara taksit ve güvenli ödeme.
                    </p>
                  </div>
                </div>
                <div className="lp-col-quarter lp-mb-4">
                  <div className="contact-card-luxury">
                    <div className="contact-icon-luxury">
                      <Truck size={30} />
                    </div>
                    <h4 className="contact-title-luxury">Hızlı & Özenli Kargo</h4>
                    <p className="info-text-luxury">
                      Türkiye'nin 81 iline darbe korumalı özel ambalajla hızlı
                      teslimat.
                    </p>
                  </div>
                </div>
                <div className="lp-col-quarter lp-mb-4">
                  <div className="contact-card-luxury">
                    <div className="contact-icon-luxury">
                      <RefreshCw size={30} />
                    </div>
                    <h4 className="contact-title-luxury">14 Gün Kolay İade</h4>
                    <p className="info-text-luxury">
                      Mesafeli satış mevzuatına uygun şeffaf değişim ve koşulsuz
                      iade hakkı.
                    </p>
                  </div>
                </div>
              </div>

              {/* iyzico Güvenli Ödeme ve Yasal Sözleşmeler Kutusu */}
              <div className="lp-iyzico-trust-box">
                <div className="lp-iyzico-trust-text">
                  <h4>
                    <ShieldCheck size={22} color="var(--lp-accent)" />
                    <span>iyzico Korumalı Alışveriş & Yasal Güvence</span>
                  </h4>
                  <p>
                    <strong>{businessInfo.storeDomain}</strong> üzerinden
                    gerçekleştireceğiniz tüm işlemler 6502 sayılı Tüketicinin
                    Korunması Hakkında Kanun ve 6698 sayılı KVKK kapsamında
                    korunmaktadır.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.65rem",
                      marginTop: "1rem",
                    }}
                  >
                    <button
                      type="button"
                      className="btn-cookie-secondary"
                      onClick={() => navigateToPage("legal", "mesafeli-satis")}
                    >
                      Mesafeli Satış Sözleşmesi
                    </button>
                    <button
                      type="button"
                      className="btn-cookie-secondary"
                      onClick={() => navigateToPage("legal", "on-bilgilendirme")}
                    >
                      Ön Bilgilendirme Formu
                    </button>
                    <button
                      type="button"
                      className="btn-cookie-secondary"
                      onClick={() => navigateToPage("legal", "iptal-iade")}
                    >
                      İptal & İade Koşulları
                    </button>
                  </div>
                </div>
                <div className="lp-iyzico-logos-wrap">
                  <img
                    src="/footer_iyzico_ile_ode/White/logo_band_white.svg"
                    alt="iyzico Korumalı Ödeme"
                    className="lp-iyzico-band-img"
                  />
                </div>
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SAYFA 4: EĞİTİM & AKADEMİ (egitim.ahmettosunluxury.com)
          ═══════════════════════════════════════════════════════════════════════ */}
      {activePage === "academy" && (
        <main className="lp-page-view">
          <section className="lp-page-hero">
            <img
              src="/images/img_clsn_2.jpeg"
              alt="Ahmet Tosun Luxury Academy"
              className="lp-page-hero-bg"
            />
            <div className="lp-page-hero-overlay"></div>
            <div className="lp-container" style={{ textAlign: "center" }}>
              <div className="lp-breadcrumb">
                <button type="button" onClick={() => navigateToPage("home")}>
                  Ana Sayfa
                </button>
                <span>/</span>
                <span>Eğitim & Akademi</span>
              </div>
              <h1 className="lp-page-hero-title">Ahmet Tosun Luxury Academy</h1>
              <p className="lp-page-hero-subtitle">
                Geleneksel ustalığı modern anatomik kesim bilimiyle birleştiren
                online ve yüz yüze sertifikalı masterclass berberlik eğitimleri.
              </p>
              <div className="lp-page-hero-actions">
                <a
                  href={businessInfo.academyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-hero-luxury-primary"
                  style={{ textDecoration: "none" }}
                >
                  <GraduationCap size={18} />
                  <span>
                    Eğitim Platformuna Git ({businessInfo.academyDomain})
                  </span>
                  <ExternalLink size={16} />
                </a>
                <button
                  type="button"
                  className="btn-hero-luxury"
                  onClick={handleBookAppointment}
                >
                  <Calendar size={18} />
                  <span>Salondan Randevu Al</span>
                </button>
              </div>
            </div>
          </section>

          <section className="section-ecosystem">
            <div className="lp-container">
              <div className="section-header-luxury">
                <span className="section-kicker">AKADEMİ MÜFREDATI</span>
                <h2 className="section-title-luxury">
                  Masterclass Eğitim Programları
                </h2>
                <p className="section-subtitle-luxury">
                  Kariyerini bir üst seviyeye taşımak isteyen saç tasarımcıları
                  için hazırlanmış kapsamlı eğitim modülleri
                </p>
              </div>

              <div className="lp-services-grid">
                {academyPrograms.map((prog) => (
                  <div className="lp-service-detail-card" key={prog.id}>
                    <div className="lp-service-card-media">
                      <img src={prog.image} alt={prog.title} />
                      <span className="lp-catalog-tag">{prog.tag}</span>
                    </div>
                    <div className="lp-service-card-body">
                      <h3
                        className="service-title-luxury"
                        style={{ textAlign: "left", marginBottom: "0.6rem" }}
                      >
                        {prog.title}
                      </h3>
                      <p
                        className="service-description-luxury"
                        style={{ textAlign: "left", marginBottom: "1rem" }}
                      >
                        {prog.desc}
                      </p>
                      <ul className="pillar-features">
                        {prog.highlights.map((h, i) => (
                          <li key={i}>
                            <CheckCircle2 size={16} color="var(--lp-accent)" />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                      <a
                        href={businessInfo.academyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-pillar-primary"
                      >
                        <BookOpen size={16} />
                        <span>Eğitime Başvur & İncele</span>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              <div className="lp-iyzico-trust-box">
                <div className="lp-iyzico-trust-text">
                  <h4>
                    <Award size={22} color="var(--lp-accent)" />
                    <span>
                      Sertifikalı Akademi & iyzico ile Güvenli Online Kayıt
                    </span>
                  </h4>
                  <p>
                    <strong>{businessInfo.academyDomain}</strong> üzerinden
                    katılacağınız online video eğitimlere anında erişim
                    sağlayabilir, kredi kartına taksit seçenekleriyle güvenli
                    şekilde kaydınızı tamamlayabilirsiniz.
                  </p>
                </div>
                <div className="lp-iyzico-logos-wrap">
                  <img
                    src="/footer_iyzico_ile_ode/White/logo_band_white.svg"
                    alt="iyzico Korumalı Ödeme"
                    className="lp-iyzico-band-img"
                  />
                </div>
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SAYFA 5: GALERİ & LOOKBOOK (GALLERY PAGE)
          ═══════════════════════════════════════════════════════════════════════ */}
      {activePage === "gallery" && (
        <main className="lp-page-view">
          <section className="lp-page-hero">
            <img
              src="/images/img_5.jpeg"
              alt="Stil Galerisi"
              className="lp-page-hero-bg"
            />
            <div className="lp-page-hero-overlay"></div>
            <div className="lp-container" style={{ textAlign: "center" }}>
              <div className="lp-breadcrumb">
                <button type="button" onClick={() => navigateToPage("home")}>
                  Ana Sayfa
                </button>
                <span>/</span>
                <span>Stil Galerisi & Lookbook</span>
              </div>
              <h1 className="lp-page-hero-title">Stil Galerisi & Lookbook</h1>
              <p className="lp-page-hero-subtitle">
                Mükemmelliğin detayları: İmza saç ve sakal tasarımlarımız, VIP
                salon atmosferimiz ve uzman ekibimizin çalışmaları.
              </p>
              <div className="lp-page-hero-actions">
                <button
                  type="button"
                  className="btn-hero-luxury-primary"
                  onClick={handleBookAppointment}
                >
                  <Calendar size={18} />
                  <span>Tarzınız İçin Randevu Alın</span>
                </button>
                <a
                  href={`https://instagram.com/${businessInfo.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-hero-luxury"
                >
                  <BsInstagram size={17} />
                  <span>@{businessInfo.instagram}</span>
                </a>
              </div>
            </div>
          </section>

          <section className="section-gallery luxury-gallery">
            <div className="lp-container">
              <div className="lp-filter-tabs">
                <button
                  type="button"
                  className={`lp-filter-btn ${
                    galleryFilter === "all" ? "active" : ""
                  }`}
                  onClick={() => setGalleryFilter("all")}
                >
                  Tümü ({galleryImages.length})
                </button>
                <button
                  type="button"
                  className={`lp-filter-btn ${
                    galleryFilter === "tasarim" ? "active" : ""
                  }`}
                  onClick={() => setGalleryFilter("tasarim")}
                >
                  Saç & Sakal Tasarımları
                </button>
                <button
                  type="button"
                  className={`lp-filter-btn ${
                    galleryFilter === "salon" ? "active" : ""
                  }`}
                  onClick={() => setGalleryFilter("salon")}
                >
                  Salon & VIP Atmosfer
                </button>
                <button
                  type="button"
                  className={`lp-filter-btn ${
                    galleryFilter === "ekip" ? "active" : ""
                  }`}
                  onClick={() => setGalleryFilter("ekip")}
                >
                  Uygulama & Detaylar
                </button>
              </div>

              <div className="lp-gallery-grid-full">
                {filteredGallery.map((item) => (
                  <div
                    key={item.id}
                    className="lp-gallery-grid-card"
                    onClick={() => setLightboxImage(item)}
                  >
                    <img src={item.url} alt={item.alt} />
                    <div
                      className="gallery-overlay-luxury"
                      style={{ transform: "translateY(0)" }}
                    >
                      <span>{item.alt}</span>
                      <Eye size={18} color="var(--lp-accent)" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SAYFA 6: KURUMSAL / HAKKIMIZDA (ABOUT PAGE)
          ═══════════════════════════════════════════════════════════════════════ */}
      {activePage === "about" && (
        <main className="lp-page-view">
          <section className="lp-page-hero">
            <img
              src="/images/img_6.jpeg"
              alt="Hakkımızda"
              className="lp-page-hero-bg"
            />
            <div className="lp-page-hero-overlay"></div>
            <div className="lp-container" style={{ textAlign: "center" }}>
              <div className="lp-breadcrumb">
                <button type="button" onClick={() => navigateToPage("home")}>
                  Ana Sayfa
                </button>
                <span>/</span>
                <span>Kurumsal & Hakkımızda</span>
              </div>
              <h1 className="lp-page-hero-title">Kurumsal & Marka Hikayemiz</h1>
              <p className="lp-page-hero-subtitle">
                Geleneksel berberlik kültürünü modern stil, e-ticaret ve
                akademi vizyonuyla geleceğe taşıyan Ahmet Tosun Luxury.
              </p>
              <div className="lp-page-hero-actions">
                <button
                  type="button"
                  className="btn-hero-luxury-primary"
                  onClick={handleBookAppointment}
                >
                  <Calendar size={18} />
                  <span>Randevu Al</span>
                </button>
                <button
                  type="button"
                  className="btn-hero-luxury"
                  onClick={() => navigateToPage("legal", "kvkk")}
                >
                  <FileText size={18} />
                  <span>Resmi Politikalar & KVKK</span>
                </button>
              </div>
            </div>
          </section>

          <section id="about" className="section-about luxury-about">
            <div className="lp-container">
              <div className="lp-row lp-align-center lp-mb-5">
                <div className="lp-col-half lp-mb-4">
                  <div className="about-content">
                    <span className="section-kicker">
                      WHERE TRADITION MEETS MODERN STYLE
                    </span>
                    <h2 className="about-heading">Ahmet Tosun Luxury</h2>
                    <p className="about-text">
                      Yılların deneyimi ve tutkusuyla erkek bakımında öncü bir
                      marka olan <strong>Ahmet Tosun Luxury</strong>, Denizli /
                      Çamlık'ta konumlanan VIP erkek bakım stüdyosu, resmi
                      e-ticaret mağazası (
                      <strong>{businessInfo.storeDomain}</strong>) ve mesleki
                      eğitim akademisi (
                      <strong>{businessInfo.academyDomain}</strong>) ile 360
                      derece kurumsal hizmet sunmaktadır.
                    </p>
                    <p className="about-text">
                      Modern anatomik kesim teknikleri ve geleneksel ustalığı
                      bir araya getirerek, her misafirimize özel ve unutulmaz
                      bir deneyim tasarlıyoruz. Uzman ekibimiz, yüz ve kafa
                      morfolojinize en uygun saç, sakal, kaş ve cilt bakım
                      protokollerini titizlikle uygular.
                    </p>
                    <p className="about-text">
                      Üst düzey sterilizasyon standartlarımız, tek kullanımlık
                      hijyen kitlerimiz, VIP odalarımız ve dünya standartlarındaki
                      kozmetik ürünlerimizle kendinizi ayrıcalıklı hissetmeniz
                      için buradayız.
                    </p>
                  </div>
                </div>
                <div className="lp-col-half lp-mb-4">
                  <div className="about-image-container">
                    <img
                      src="/images/img_6.jpeg"
                      alt="Ahmet Tosun Luxury Salon"
                      className="about-image"
                    />
                  </div>
                </div>
              </div>

              {/* Kurumsal Künye Tablosu */}
              <div className="lp-legal-document-box">
                <div className="lp-legal-doc-header">
                  <h2>Kurumsal Kimlik & Resmi İletişim Künyesi</h2>
                  <div className="lp-legal-doc-meta">
                    Randevu, E-Ticaret ve Akademi Platformlarımızın Resmi
                    Bilgileri
                  </div>
                </div>
                <table className="lp-legal-info-table">
                  <tbody>
                    <tr>
                      <th>Marka / Ticari Ünvan</th>
                      <td>{businessInfo.legalTitle}</td>
                    </tr>
                    <tr>
                      <th>Merkez Stüdyo Adresi</th>
                      <td>{businessInfo.address}</td>
                    </tr>
                    <tr>
                      <th>Müşteri Hizmetleri & VIP Hat</th>
                      <td>{businessInfo.phone}</td>
                    </tr>
                    <tr>
                      <th>Resmi E-Posta / KEP</th>
                      <td>{businessInfo.email}</td>
                    </tr>
                    <tr>
                      <th>Çalışma Saatleri</th>
                      <td>{businessInfo.workingHours} (Pazar: Kapalı)</td>
                    </tr>
                    <tr>
                      <th>Resmi Dijital Platformlar</th>
                      <td>
                        • Online Randevu & Kurumsal Web: ahmettosunluxury.com
                        <br />• Resmi E-Ticaret Mağazası:{" "}
                        <a
                          href={businessInfo.storeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "var(--lp-accent)" }}
                        >
                          {businessInfo.storeDomain}
                        </a>
                        <br />• Resmi Eğitim & Akademi Platformu:{" "}
                        <a
                          href={businessInfo.academyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "var(--lp-accent)" }}
                        >
                          {businessInfo.academyDomain}
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SAYFA 7: İLETİŞİM & ULAŞIM (CONTACT PAGE)
          ═══════════════════════════════════════════════════════════════════════ */}
      {activePage === "contact" && (
        <main className="lp-page-view">
          <section className="lp-page-hero">
            <img
              src="/images/img_4.jpeg"
              alt="İletişim"
              className="lp-page-hero-bg"
            />
            <div className="lp-page-hero-overlay"></div>
            <div className="lp-container" style={{ textAlign: "center" }}>
              <div className="lp-breadcrumb">
                <button type="button" onClick={() => navigateToPage("home")}>
                  Ana Sayfa
                </button>
                <span>/</span>
                <span>İletişim & Ulaşım</span>
              </div>
              <h1 className="lp-page-hero-title">İletişim & VIP Rezervasyon</h1>
              <p className="lp-page-hero-subtitle">
                Salon randevularınız, mağaza siparişleriniz ve akademi eğitim
                başvurularınız için bizimle doğrudan iletişime geçebilirsiniz.
              </p>
              <div className="lp-page-hero-actions">
                <button
                  type="button"
                  className="btn-hero-luxury-primary"
                  onClick={handleBookAppointment}
                >
                  <Calendar size={18} />
                  <span>Online Randevu Al</span>
                </button>
                <a
                  href={`https://wa.me/${businessInfo.whatsapp}?text=Merhaba,%20Ahmet%20Tosun%20Luxury%20hizmetleri%20hakkında%20bilgi%20almak%20istiyorum.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-hero-luxury"
                >
                  <BsWhatsapp size={18} />
                  <span>WhatsApp VIP Destek</span>
                </a>
              </div>
            </div>
          </section>

          <section id="contact" className="section-contact luxury-contact">
            <div className="lp-container">
              <div className="lp-row lp-justify-center lp-mb-5">
                <div className="lp-col-quarter lp-mb-4">
                  <div className="contact-card-luxury">
                    <div className="contact-icon-luxury">
                      <Phone size={28} />
                    </div>
                    <h3 className="contact-title-luxury">Telefon</h3>
                    <a
                      href={`tel:${businessInfo.phone.replace(/\s/g, "")}`}
                      className="contact-link-luxury"
                    >
                      {businessInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="lp-col-quarter lp-mb-4">
                  <div className="contact-card-luxury">
                    <div className="contact-icon-luxury">
                      <BsWhatsapp size={28} />
                    </div>
                    <h3 className="contact-title-luxury">WhatsApp</h3>
                    <a
                      href={`https://wa.me/${businessInfo.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link-luxury"
                    >
                      Hızlı Mesaj Gönder
                    </a>
                  </div>
                </div>

                <div className="lp-col-quarter lp-mb-4">
                  <div className="contact-card-luxury">
                    <div className="contact-icon-luxury">
                      <Mail size={28} />
                    </div>
                    <h3 className="contact-title-luxury">E-Posta</h3>
                    <a
                      href={`mailto:${businessInfo.email}`}
                      className="contact-link-luxury"
                    >
                      {businessInfo.email}
                    </a>
                  </div>
                </div>

                <div className="lp-col-quarter lp-mb-4">
                  <div className="contact-card-luxury">
                    <div className="contact-icon-luxury">
                      <BsInstagram size={28} />
                    </div>
                    <h3 className="contact-title-luxury">Instagram</h3>
                    <a
                      href={`https://instagram.com/${businessInfo.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link-luxury"
                    >
                      @{businessInfo.instagram}
                    </a>
                  </div>
                </div>
              </div>

              <div className="lp-row">
                <div className="lp-col-half lp-mb-4">
                  <div className="info-card-luxury">
                    <div className="info-icon-luxury">
                      <MapPin size={26} />
                    </div>
                    <div>
                      <h4 className="info-title-luxury">Stüdyo Adresi</h4>
                      <p className="info-text-luxury">{businessInfo.address}</p>
                    </div>
                  </div>

                  <div className="info-card-luxury lp-mt-4">
                    <div className="info-icon-luxury">
                      <Clock size={26} />
                    </div>
                    <div>
                      <h4 className="info-title-luxury">Çalışma Saatleri</h4>
                      <p className="info-text-luxury">
                        {businessInfo.workingHours}
                      </p>
                      <p className="info-text-luxury">Pazar: Kapalı</p>
                    </div>
                  </div>

                  <div className="info-card-luxury lp-mt-4">
                    <div className="info-icon-luxury">
                      <ShoppingBag size={26} />
                    </div>
                    <div>
                      <h4 className="info-title-luxury">
                        Mağaza & Akademi Destek
                      </h4>
                      <p className="info-text-luxury">
                        Sipariş ve eğitim destek talepleriniz için:{" "}
                        <strong>{businessInfo.email}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="lp-col-half lp-mb-4">
                  <div className="map-container-luxury">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3154.993744921267!2d29.089392076568792!3d37.74329097199329!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c73f4232b6579d%3A0x8e719b622bdc28df!2sAHMET%20TOSUN%20LUXURY!5e0!3m2!1str!2str!4v1767281626421!5m2!1str!2str"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Ahmet Tosun Luxury Konum"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SAYFA 8: YASAL METİNLER, KVKK & SÖZLEŞMELER MERKEZİ (LEGAL PAGE)
          ═══════════════════════════════════════════════════════════════════════ */}
      {activePage === "legal" && (
        <main className="lp-page-view">
          <section className="lp-page-hero">
            <img
              src="/images/img_9.webp"
              alt="Yasal Metinler ve Sözleşmeler"
              className="lp-page-hero-bg"
            />
            <div className="lp-page-hero-overlay"></div>
            <div className="lp-container" style={{ textAlign: "center" }}>
              <div className="lp-breadcrumb">
                <button type="button" onClick={() => navigateToPage("home")}>
                  Ana Sayfa
                </button>
                <span>/</span>
                <span>Yasal Metinler & Sözleşmeler</span>
              </div>
              <h1 className="lp-page-hero-title">
                Kurumsal Politikalar & Sözleşmeler
              </h1>
              <p className="lp-page-hero-subtitle">
                6698 Sayılı KVKK Aydınlatma Metni, Mesafeli Satış Sözleşmesi,
                İptal & İade Koşulları ve Gizlilik/Çerez Politikamız.
              </p>
            </div>
          </section>

          <section className="lp-legal-section">
            <div className="lp-container">
              <div className="lp-legal-layout">
                {/* Sol Menü */}
                <aside className="lp-legal-sidebar">
                  <div className="lp-legal-nav-title">Yasal Belgeler</div>
                  <button
                    type="button"
                    className={`lp-legal-tab-btn ${
                      legalTab === "kvkk" ? "active" : ""
                    }`}
                    onClick={() => navigateToPage("legal", "kvkk")}
                  >
                    <ShieldCheck size={16} />
                    <span>KVKK Aydınlatma Metni</span>
                  </button>
                  <button
                    type="button"
                    className={`lp-legal-tab-btn ${
                      legalTab === "mesafeli-satis" ? "active" : ""
                    }`}
                    onClick={() => navigateToPage("legal", "mesafeli-satis")}
                  >
                    <FileText size={16} />
                    <span>Mesafeli Satış Sözleşmesi</span>
                  </button>
                  <button
                    type="button"
                    className={`lp-legal-tab-btn ${
                      legalTab === "on-bilgilendirme" ? "active" : ""
                    }`}
                    onClick={() => navigateToPage("legal", "on-bilgilendirme")}
                  >
                    <BookOpen size={16} />
                    <span>Ön Bilgilendirme Formu</span>
                  </button>
                  <button
                    type="button"
                    className={`lp-legal-tab-btn ${
                      legalTab === "iptal-iade" ? "active" : ""
                    }`}
                    onClick={() => navigateToPage("legal", "iptal-iade")}
                  >
                    <RefreshCw size={16} />
                    <span>İptal, İade & Teslimat</span>
                  </button>
                  <button
                    type="button"
                    className={`lp-legal-tab-btn ${
                      legalTab === "cerez-politikasi" ? "active" : ""
                    }`}
                    onClick={() => navigateToPage("legal", "cerez-politikasi")}
                  >
                    <Cookie size={16} />
                    <span>Gizlilik ve Çerez Politikası</span>
                  </button>
                  <button
                    type="button"
                    className={`lp-legal-tab-btn ${
                      legalTab === "kullanim-kosullari" ? "active" : ""
                    }`}
                    onClick={() =>
                      navigateToPage("legal", "kullanim-kosullari")
                    }
                  >
                    <Lock size={16} />
                    <span>Kullanım & Üyelik Koşulları</span>
                  </button>
                </aside>

                {/* Sağ Belge İçeriği */}
                <div className="lp-legal-document-box">
                  {legalTab === "kvkk" && (
                    <div className="lp-legal-prose">
                      <div className="lp-legal-doc-header">
                        <h2>
                          6698 Sayılı Kişisel Verilerin Korunması Kanunu (KVKK)
                          Aydınlatma Metni
                        </h2>
                        <div className="lp-legal-doc-meta">
                          Veri Sorumlusu: {businessInfo.legalTitle} • Son
                          Güncelleme: 2026
                        </div>
                      </div>

                      <h3>1. Veri Sorumlusunun Kimliği</h3>
                      <p>
                        6698 sayılı Kişisel Verilerin Korunması Kanunu (“KVKK”)
                        uyarınca, kişisel verileriniz; veri sorumlusu sıfatıyla{" "}
                        <strong>{businessInfo.legalTitle}</strong> (Adres:{" "}
                        {businessInfo.address}, Telefon: {businessInfo.phone},
                        E-Posta: {businessInfo.email}) tarafından aşağıda
                        açıklanan kapsamda hukuka ve dürüstlük kurallarına uygun
                        olarak işlenmektedir.
                      </p>

                      <h3>2. İşlenen Kişisel Veriler ve İşleme Amaçları</h3>
                      <p>
                        Online randevu sistemimiz,{" "}
                        <strong>{businessInfo.storeDomain}</strong> e-ticaret
                        mağazamız ve{" "}
                        <strong>{businessInfo.academyDomain}</strong> eğitim
                        platformumuz üzerinden toplanan Kimlik (Ad, Soyad),
                        İletişim (Telefon, E-Posta, Teslimat/Fatura Adresi),
                        Müşteri İşlem (Randevu geçmişi, sipariş ve eğitim kayıt
                        bilgileri) ve İşlem Güvenliği verileriniz şu amaçlarla
                        işlenmektedir:
                      </p>
                      <ul>
                        <li>
                          Salon randevularının oluşturulması, hatırlatma SMS ve
                          anlık bildirimlerinin (Push Notification) iletilmesi,
                        </li>
                        <li>
                          E-ticaret mağazamızdan satın alınan ürünlerin faturalandırılması,
                          kargolanması ve satış sonrası destek süreçlerinin
                          yürütülmesi,
                        </li>
                        <li>
                          Akademi eğitim platformumuzdaki kurs kayıtlarının,
                          sertifikasyon süreçlerinin ve kullanıcı hesaplarının
                          yönetilmesi,
                        </li>
                        <li>
                          iyzico ödeme kuruluşu aracılığıyla güvenli tahsilat ve
                          dolandırıcılık önleme kontrollerinin yapılması.
                        </li>
                      </ul>

                      <h3>3. Kişisel Verilerin Aktarılması</h3>
                      <p>
                        Kişisel verileriniz; yalnızca hizmetin ifası için
                        zorunlu olduğu ölçüde BDDK lisanslı ödeme kuruluşu{" "}
                        <strong>iyzico Ödeme ve Elektronik Para Hizmetleri A.Ş.</strong>,
                        anlaşmalı kargo firmaları, mali müşavirlik/e-fatura
                        altyapı sağlayıcıları ve yetkili kamu kurumları ile
                        KVKK’nın 8. ve 9. maddelerine uygun olarak
                        paylaşılmaktadır. Kredi kartı bilgileriniz sistemlerimizde
                        kesinlikle saklanmamaktadır.
                      </p>

                      <h3>4. KVKK Madde 11 Kapsamındaki Haklarınız</h3>
                      <p>
                        Kişisel veri sahibi olarak KVKK’nın 11. maddesi uyarınca
                        verilerinizin işlenip işlenmediğini öğrenme, düzeltilmesini
                        veya silinmesini talep etme haklarınızı{" "}
                        <strong>{businessInfo.email}</strong> adresine yazılı
                        başvuruda bulunarak kullanabilirsiniz.
                      </p>
                    </div>
                  )}

                  {legalTab === "mesafeli-satis" && (
                    <div className="lp-legal-prose">
                      <div className="lp-legal-doc-header">
                        <h2>Mesafeli Satış Sözleşmesi (Uzaktan Satış)</h2>
                        <div className="lp-legal-doc-meta">
                          6502 Sayılı Tüketicinin Korunması Hakkında Kanun ve
                          Mesafeli Sözleşmeler Yönetmeliği Uyarınca
                        </div>
                      </div>

                      <h3>Madde 1 - Taraflar</h3>
                      <p>
                        <strong>1.1. SATICI / HİZMET SAĞLAYICI:</strong>
                        <br />
                        Ünvan: {businessInfo.legalTitle}
                        <br />
                        Adres: {businessInfo.address}
                        <br />
                        Telefon: {businessInfo.phone} • E-Posta:{" "}
                        {businessInfo.email}
                        <br />
                        E-Ticaret Platformu: {businessInfo.storeUrl} • Eğitim
                        Platformu: {businessInfo.academyUrl}
                      </p>
                      <p>
                        <strong>1.2. ALICI (TÜKETİCİ):</strong> Elektronik
                        ortamda sipariş veya eğitim kaydı oluşturan müşteri.
                      </p>

                      <h3>Madde 2 - Sözleşmenin Konusu</h3>
                      <p>
                        İşbu sözleşmenin konusu, ALICI’nın SATICI’ya ait{" "}
                        <strong>{businessInfo.storeDomain}</strong> üzerinden
                        sipariş verdiği kozmetik/kişisel bakım ürünlerinin veya{" "}
                        <strong>{businessInfo.academyDomain}</strong> üzerinden
                        satın aldığı eğitim hizmetlerinin satışı, teslimi ve
                        ifası ile ilgili tarafların hak ve yükümlülüklerinin
                        belirlenmesidir.
                      </p>

                      <h3>Madde 3 - Ödeme ve Teslimat Şartları</h3>
                      <ul>
                        <li>
                          Ödemeler <strong>iyzico</strong> güvenli ödeme
                          altyapısı üzerinden kredi kartı veya banka kartı ile
                          gerçekleştirilir.
                        </li>
                        <li>
                          Fiziksel mağaza ürünleri, sipariş onayından itibaren
                          en geç 1-3 iş günü içerisinde kargoya teslim edilir;
                          yasal azami teslim süresi 30 günü aşamaz.
                        </li>
                        <li>
                          Online dijital eğitim paketleri, ödeme onayının hemen
                          ardından ALICI’nın akademi hesabına tanımlanır.
                        </li>
                      </ul>

                      <h3>Madde 4 - Cayma Hakkı ve İstisnaları</h3>
                      <p>
                        ALICI, fiziksel ürün tesliminden itibaren{" "}
                        <strong>14 (on dört) gün</strong> içerisinde hiçbir
                        gerekçe göstermeksizin cayma hakkını kullanabilir. Ancak
                        Mesafeli Sözleşmeler Yönetmeliği Madde 15 uyarınca;{" "}
                        <strong>
                          tesliminden sonra koruyucu ambalajı, bandı veya mührü
                          açılmış olan kozmetik ve kişisel bakım ürünlerinin
                        </strong>{" "}
                        sağlık ve hijyen açısından iadesi kabul edilmemektedir.
                        Elektronik ortamda anında ifa edilen dijital eğitim
                        içeriklerinde erişim başladıktan sonra cayma hakkı
                        kullanılamaz.
                      </p>

                      <h3>Madde 5 - Uyuşmazlıkların Çözümü</h3>
                      <p>
                        İşbu sözleşmeden doğabilecek uyuşmazlıklarda Ticaret
                        Bakanlığınca ilan edilen parasal sınırlara göre ALICI
                        veya SATICI’nın yerleşim yerindeki (Denizli) Tüketici
                        Hakem Heyetleri ve Tüketici Mahkemeleri yetkilidir.
                      </p>
                    </div>
                  )}

                  {legalTab === "on-bilgilendirme" && (
                    <div className="lp-legal-prose">
                      <div className="lp-legal-doc-header">
                        <h2>Ön Bilgilendirme Formu</h2>
                        <div className="lp-legal-doc-meta">
                          Sipariş ve Ödeme Öncesi Tüketici Bilgilendirme Belgesi
                        </div>
                      </div>

                      <h3>1. Satıcı ve Platform Bilgileri</h3>
                      <p>
                        Satıcı: {businessInfo.legalTitle} ({businessInfo.address}
                        ). İletişim: {businessInfo.phone} / {businessInfo.email}.
                      </p>

                      <h3>2. Ürün / Hizmet Temel Nitelikleri ve Fiyat</h3>
                      <p>
                        Satışa sunulan ürün veya eğitim programının adı, adedi,
                        KDV dahil toplam satış fiyatı ve kargo ücreti ödeme
                        sayfasında (sepet adımında) açıkça gösterilmekte ve
                        ALICI’nın onayına sunulmaktadır.
                      </p>

                      <h3>3. Güvenli Ödeme Altyapısı (iyzico)</h3>
                      <p>
                        Tüm ödemeler PCI-DSS 1. Seviye güvenlik sertifikasına
                        sahip <strong>iyzico</strong> altyapısı üzerinden 3D
                        Secure doğrulaması ile tahsil edilir.
                      </p>
                    </div>
                  )}

                  {legalTab === "iptal-iade" && (
                    <div className="lp-legal-prose">
                      <div className="lp-legal-doc-header">
                        <h2>İptal, İade ve Teslimat Koşulları</h2>
                        <div className="lp-legal-doc-meta">
                          Salon Randevu, E-Ticaret Mağaza ve Akademi İade
                          Süreçleri
                        </div>
                      </div>

                      <h3>1. Salon Randevu İptal ve Değişiklik Kuralları</h3>
                      <p>
                        Online oluşturulan randevular, randevu saatinden en az{" "}
                        <strong>2 saat öncesine kadar</strong> müşteri panelinden
                        veya {businessInfo.phone} numaralı hattımızdan ücretsiz
                        olarak iptal edilebilir veya ileri bir tarihe
                        ertelenebilir.
                      </p>

                      <h3>2. Mağaza ({businessInfo.storeDomain}) İade Süreci</h3>
                      <ul>
                        <li>
                          Ambalajı, koruyucu bandı veya kutusu açılmamış,
                          kullanılmamış ürünleri teslim tarihinden itibaren 14
                          gün içinde iade edebilirsiniz.
                        </li>
                        <li>
                          İade edilen ürün tarafımıza ulaşıp incelendikten sonra
                          en geç 3-7 iş günü içerisinde <strong>iyzico</strong>{" "}
                          üzerinden ödeme yaptığınız karta iade talimatı
                          verilir.
                        </li>
                      </ul>

                      <h3>
                        3. Akademi ({businessInfo.academyDomain}) İptal Süreci
                      </h3>
                      <p>
                        Yüz yüze stüdyo eğitimlerinde eğitim tarihinden 7 gün
                        öncesine kadar yapılan iptallerde kesintisiz iade veya
                        dönem değişikliği yapılır. Online video eğitimlerde ise
                        ders videoları izlenmeye başlanmadan önce yapılan
                        başvurularda iade gerçekleştirilir.
                      </p>
                    </div>
                  )}

                  {legalTab === "cerez-politikasi" && (
                    <div className="lp-legal-prose">
                      <div className="lp-legal-doc-header">
                        <h2>Gizlilik ve Çerez (Cookie) Politikası</h2>
                        <div className="lp-legal-doc-meta">
                          Çerez Türleri, Kullanım Amaçları ve Tercih Yönetimi
                        </div>
                      </div>

                      <h3>1. Çerez (Cookie) Nedir?</h3>
                      <p>
                        Çerezler, web sitemizi ve alt platformlarımızı ziyaret
                        ettiğinizde tarayıcınız aracılığıyla cihazınıza
                        kaydedilen küçük metin dosyalarıdır.
                      </p>

                      <h3>2. Kullandığımız Çerez Kategorileri</h3>
                      <ul>
                        <li>
                          <strong>Zorunlu Çerezler:</strong> Oturum açma,
                          randevu oluşturma, güvenlik ve çerez tercihlerinizin
                          hatırlanması için teknik olarak zorunludur.
                        </li>
                        <li>
                          <strong>İşlevsel Çerezler:</strong> Bildirim izinleri
                          (FCM token) ve kullanıcı arayüzü tercihlerinizin
                          korunmasını sağlar.
                        </li>
                        <li>
                          <strong>Performans ve Analitik Çerezler:</strong> Site
                          hızını ve ziyaretçi deneyimini iyileştirmemize yardımcı
                          olur.
                        </li>
                      </ul>

                      <div style={{ marginTop: "1.5rem" }}>
                        <button
                          type="button"
                          className="btn-pillar-primary"
                          style={{ width: "auto" }}
                          onClick={() => setCookieModalVisible(true)}
                        >
                          <Cookie size={16} />
                          <span>Çerez Tercihlerimi Güncelle</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {legalTab === "kullanim-kosullari" && (
                    <div className="lp-legal-prose">
                      <div className="lp-legal-doc-header">
                        <h2>Web Sitesi Kullanım ve Üyelik Koşulları</h2>
                        <div className="lp-legal-doc-meta">
                          Fikri Mülkiyet ve Platform Kullanım Şartları
                        </div>
                      </div>

                      <h3>1. Genel Koşullar</h3>
                      <p>
                        Bu web sitesini ve bağlı alt alan adlarını (
                        {businessInfo.storeDomain}, {businessInfo.academyDomain})
                        ziyaret eden her kullanıcı işbu kullanım koşullarını
                        kabul etmiş sayılır.
                      </p>

                      <h3>2. Fikri Mülkiyet Hakları</h3>
                      <p>
                        Sitede yer alan “Ahmet Tosun Luxury” markası, logolar,
                        fotoğraflar, eğitim videoları ve tasarımlar{" "}
                        {businessInfo.legalTitle}’a aittir; izinsiz kopyalanamaz
                        veya çoğaltılamaz.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ── KURUMSAL ÇOK SÜTUNLU FOOTER + İYZİCO GÜVENLİK BANDI ── */}
      <footer className="luxury-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            {/* Sütun 1: Marka & Kurumsal */}
            <div className="lp-footer-col">
              <img
                src="/images/logo_wide.png"
                alt="Ahmet Tosun Luxury"
                className="footer-logo"
              />
              <div className="footer-slogan">{businessInfo.slogan}</div>
              <p className="footer-desc-text">
                Denizli / Çamlık VIP Erkek Bakım Stüdyosu, Resmi E-Ticaret
                Mağazası ve Profesyonel Berberlik Akademisi.
              </p>
              <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn-cookie-accept"
                  onClick={handleBookAppointment}
                >
                  <Calendar size={14} className="btn-icon-inline" /> Randevu Al
                </button>
                <button
                  type="button"
                  className="btn-cookie-secondary"
                  onClick={() => setInstallModalVisible(true)}
                >
                  <Download size={14} className="btn-icon-inline" /> Uygulama
                </button>
              </div>
            </div>

            {/* Sütun 2: Kurumsal Sayfalar */}
            <div className="lp-footer-col">
              <h4>Kurumsal Menü</h4>
              <ul className="lp-footer-links">
                <li>
                  <button type="button" onClick={() => navigateToPage("home")}>
                    <ChevronRight size={14} /> Ana Sayfa
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigateToPage("services")}
                  >
                    <ChevronRight size={14} /> Hizmetlerimiz
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigateToPage("gallery")}
                  >
                    <ChevronRight size={14} /> Stil Galerisi & Lookbook
                  </button>
                </li>
                <li>
                  <button type="button" onClick={() => navigateToPage("about")}>
                    <ChevronRight size={14} /> Kurumsal / Hakkımızda
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigateToPage("contact")}
                  >
                    <ChevronRight size={14} /> İletişim & Konum
                  </button>
                </li>
              </ul>
            </div>

            {/* Sütun 3: Marka Ekosistemi */}
            <div className="lp-footer-col">
              <h4>Dijital Ekosistem</h4>
              <ul className="lp-footer-links">
                <li>
                  <button type="button" onClick={handleBookAppointment}>
                    <Calendar size={14} /> Online Randevu Sistemi
                  </button>
                </li>
                <li>
                  <a
                    href={businessInfo.storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ShoppingBag size={14} /> Mağaza ({businessInfo.storeDomain})
                    <ExternalLink size={12} />
                  </a>
                </li>
                <li>
                  <button type="button" onClick={() => navigateToPage("store")}>
                    <ChevronRight size={14} /> Mağaza Koleksiyon Rehberi
                  </button>
                </li>
                <li>
                  <a
                    href={businessInfo.academyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GraduationCap size={14} /> Akademi (
                    {businessInfo.academyDomain})
                    <ExternalLink size={12} />
                  </a>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigateToPage("academy")}
                  >
                    <ChevronRight size={14} /> Akademi Eğitim Müfredatı
                  </button>
                </li>
              </ul>
            </div>

            {/* Sütun 4: Yasal & Sözleşmeler */}
            <div className="lp-footer-col">
              <h4>Yasal & Sözleşmeler</h4>
              <ul className="lp-footer-links">
                <li>
                  <button
                    type="button"
                    onClick={() => navigateToPage("legal", "kvkk")}
                  >
                    <ChevronRight size={14} /> KVKK Aydınlatma Metni
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigateToPage("legal", "mesafeli-satis")}
                  >
                    <ChevronRight size={14} /> Mesafeli Satış Sözleşmesi
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigateToPage("legal", "on-bilgilendirme")}
                  >
                    <ChevronRight size={14} /> Ön Bilgilendirme Formu
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigateToPage("legal", "iptal-iade")}
                  >
                    <ChevronRight size={14} /> İptal, İade & Teslimat Koşulları
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => navigateToPage("legal", "cerez-politikasi")}
                  >
                    <ChevronRight size={14} /> Gizlilik ve Çerez Politikası
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => setCookieModalVisible(true)}
                  >
                    <Cookie size={14} /> Çerez Tercihlerini Yönet
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Resmi iyzico Ödeme Güvenlik Bandı */}
          <div className="lp-footer-payment-bar">
            <div className="lp-footer-payment-info">
              <ShieldCheck size={22} color="var(--lp-accent)" />
              <span>
                <strong>{businessInfo.storeDomain}</strong> ve{" "}
                <strong>{businessInfo.academyDomain}</strong> üzerindeki tüm
                ödemeleriniz 256-Bit SSL ve{" "}
                <strong>iyzico Korumalı Alışveriş</strong> güvencesi altındadır.
              </span>
            </div>
            <div className="lp-footer-payment-logos">
              <img
                src="/footer_iyzico_ile_ode/White/logo_band_white.svg"
                alt="iyzico ile Öde"
                className="lp-iyzico-band-img"
              />
            </div>
          </div>

          {/* Alt Telif & Powered By */}
          <div className="lp-footer-bottom">
            <div>
              © 2026 {businessInfo.fullName}. Tüm hakları saklıdır.
            </div>
            <div className="footer-powered">
              Powered by{" "}
              <a
                href="https://www.vireliosoft.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Vireliosoft
              </a>{" "}
              /{" "}
              <a
                href="https://www.randevupp.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                RandevUpp
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* ── SCROLL TO TOP ── */}
      {showScrollTop && (
        <button
          type="button"
          className="scroll-to-top-btn"
          onClick={scrollToTop}
          aria-label="Yukarı Çık"
        >
          <svg
            className="progress-ring"
            width="100%"
            height="100%"
            viewBox="0 0 60 60"
          >
            <circle
              className="progress-ring-circle-bg"
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth="4"
              fill="transparent"
              r="26"
              cx="30"
              cy="30"
            />
            <circle
              ref={circleRef}
              className="progress-ring-circle"
              stroke="#d4d4d8"
              strokeWidth="4"
              fill="transparent"
              r="26"
              cx="30"
              cy="30"
              style={{
                strokeDasharray: `${2 * Math.PI * 26}`,
                strokeDashoffset: `${2 * Math.PI * 26}`,
              }}
            />
          </svg>
          <ChevronUp size={22} className="scroll-icon" />
        </button>
      )}

      {/* ── MOBİL 5'Lİ LÜKS ALT DOCK BAR (ORTADA YÜKSELTİLMİŞ RANDEVU AL) ── */}
      <div className="mobile-bottom-bar">
        <button
          type="button"
          className={`bottom-dock-item ${
            activePage === "home" ? "active" : ""
          }`}
          onClick={() => navigateToPage("home")}
        >
          <Home size={19} />
          <span>Ana Sayfa</span>
        </button>

        <button
          type="button"
          className={`bottom-dock-item ${
            activePage === "store" ? "active" : ""
          }`}
          onClick={() => navigateToPage("store")}
        >
          <ShoppingBag size={19} />
          <span>Mağaza</span>
        </button>

        <button
          type="button"
          className="bottom-dock-center-cta"
          onClick={handleBookAppointment}
        >
          <Calendar size={22} />
          <span>Randevu Al</span>
        </button>

        <button
          type="button"
          className={`bottom-dock-item ${
            activePage === "academy" ? "active" : ""
          }`}
          onClick={() => navigateToPage("academy")}
        >
          <GraduationCap size={19} />
          <span>Akademi</span>
        </button>

        <button
          type="button"
          className="bottom-dock-item"
          onClick={() => setInstallModalVisible(true)}
        >
          <Download size={19} />
          <span>Uygulama</span>
        </button>
      </div>

      {/* ── ÇEREZ (COOKIE) İZNİ BANNER ── */}
      {cookieBannerVisible && (
        <div className="lp-cookie-banner">
          <div className="lp-cookie-text">
            <h5 className="lp-cookie-title">
              <Cookie size={18} color="var(--lp-accent)" />
              <span>Çerez (Cookie) ve KVKK Gizlilik Bildirimi</span>
            </h5>
            <p className="lp-cookie-desc">
              Ahmet Tosun Luxury olarak; randevu sistemimiz,{" "}
              <strong>{businessInfo.storeDomain}</strong> ve{" "}
              <strong>{businessInfo.academyDomain}</strong> platformlarımızda
              size daha iyi bir deneyim sunmak için çerezler kullanıyoruz.
              Detaylı bilgi için{" "}
              <button
                type="button"
                onClick={() => navigateToPage("legal", "cerez-politikasi")}
              >
                Çerez Politikası
              </button>{" "}
              ve{" "}
              <button
                type="button"
                onClick={() => navigateToPage("legal", "kvkk")}
              >
                KVKK Aydınlatma Metni
              </button>{" "}
              sayfalarımızı inceleyebilirsiniz.
            </p>
          </div>
          <div className="lp-cookie-actions">
            <button
              type="button"
              className="btn-cookie-secondary"
              onClick={() => setCookieModalVisible(true)}
            >
              Çerez Ayarları
            </button>
            <button
              type="button"
              className="btn-cookie-secondary"
              onClick={handleAcceptNecessaryCookies}
            >
              Sadece Zorunlular
            </button>
            <button
              type="button"
              className="btn-cookie-accept"
              onClick={handleAcceptAllCookies}
            >
              Tümünü Kabul Et
            </button>
          </div>
        </div>
      )}

      {/* ── ÇEREZ TERCİHLERİ MODALI ── */}
      {cookieModalVisible && (
        <div
          className="lp-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setCookieModalVisible(false);
          }}
        >
          <div className="modal-content-wrapper lp-modal-box">
            <div className="modal-header-custom">
              <h5 className="modal-title">Çerez Tercihleri Merkezi</h5>
              <button
                type="button"
                className="btn-close-custom"
                onClick={() => setCookieModalVisible(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="lp-modal-body">
              <div className="modal-scroll-area">
                <div className="lp-cookie-pref-item">
                  <div>
                    <strong style={{ color: "#fff", fontSize: "0.9rem" }}>
                      Zorunlu Sistem Çerezleri
                    </strong>
                    <p
                      style={{
                        color: "var(--lp-text-muted)",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0 0",
                      }}
                    >
                      Randevu oturumu, güvenlik ve temel site fonksiyonları için
                      her zaman aktiftir.
                    </p>
                  </div>
                  <span className="nav-badge-mini">HER ZAMAN AKTİF</span>
                </div>

                <div className="lp-cookie-pref-item">
                  <div>
                    <strong style={{ color: "#fff", fontSize: "0.9rem" }}>
                      İşlevsel & Bildirim Çerezleri
                    </strong>
                    <p
                      style={{
                        color: "var(--lp-text-muted)",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0 0",
                      }}
                    >
                      Anlık randevu bildirimleri (FCM) ve dil/arayüz
                      tercihlerinizi hatırlar.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={cookiePrefs.functional}
                    onChange={(e) =>
                      setCookiePrefs({
                        ...cookiePrefs,
                        functional: e.target.checked,
                      })
                    }
                  />
                </div>

                <div className="lp-cookie-pref-item">
                  <div>
                    <strong style={{ color: "#fff", fontSize: "0.9rem" }}>
                      Analitik ve Performans Çerezleri
                    </strong>
                    <p
                      style={{
                        color: "var(--lp-text-muted)",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0 0",
                      }}
                    >
                      Ziyaretçi trafiğini anonim olarak ölçümleyerek hizmet
                      kalitemizi artırır.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={cookiePrefs.analytics}
                    onChange={(e) =>
                      setCookiePrefs({
                        ...cookiePrefs,
                        analytics: e.target.checked,
                      })
                    }
                  />
                </div>

                <div className="lp-cookie-pref-item">
                  <div>
                    <strong style={{ color: "#fff", fontSize: "0.9rem" }}>
                      Pazarlama & Kampanya Çerezleri
                    </strong>
                    <p
                      style={{
                        color: "var(--lp-text-muted)",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0 0",
                      }}
                    >
                      Mağaza ve Akademi kampanyalarını ilgi alanlarınıza göre
                      sunmamızı sağlar.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={cookiePrefs.marketing}
                    onChange={(e) =>
                      setCookiePrefs({
                        ...cookiePrefs,
                        marketing: e.target.checked,
                      })
                    }
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    marginTop: "1.25rem",
                  }}
                >
                  <button
                    type="button"
                    className="btn-pillar-outline"
                    onClick={() => saveCookieConsent(cookiePrefs)}
                  >
                    Seçimlerimi Kaydet
                  </button>
                  <button
                    type="button"
                    className="btn-pillar-primary"
                    onClick={handleAcceptAllCookies}
                  >
                    Tümünü Kabul Et
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── GALERİ LIGHTBOX MODALI ── */}
      {lightboxImage && (
        <div
          className="lp-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setLightboxImage(null);
          }}
        >
          <div className="modal-content-wrapper lp-modal-box-lg">
            <div className="modal-header-custom">
              <h5 className="modal-title">{lightboxImage.alt}</h5>
              <button
                type="button"
                className="btn-close-custom"
                onClick={() => setLightboxImage(null)}
              >
                <X size={22} />
              </button>
            </div>
            <div style={{ padding: "1rem", textAlign: "center" }}>
              <img
                src={lightboxImage.url}
                alt={lightboxImage.alt}
                style={{
                  maxWidth: "100%",
                  maxHeight: "65vh",
                  objectFit: "contain",
                }}
              />
              <div style={{ marginTop: "1rem" }}>
                <button
                  type="button"
                  className="btn-nav-book-primary"
                  onClick={() => {
                    setLightboxImage(null);
                    handleBookAppointment();
                  }}
                >
                  <Calendar size={16} />
                  <span>Bu Stil İçin Randevu Al</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MOBİL UYGULAMA & BİLDİRİM MODALI ── */}
      {installModalVisible && (
        <div
          className="lp-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setInstallModalVisible(false);
          }}
        >
          <div className="modal-content-wrapper lp-modal-box">
            <div className="modal-header-custom">
              <h5 className="modal-title">Mobil Uygulama & Bildirimler</h5>
              <button
                type="button"
                className="btn-close-custom"
                onClick={() => setInstallModalVisible(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="lp-modal-body">
              <div className="modal-scroll-area">
                <div className="install-card notification-card lp-mb-4">
                  <div className="os-header lp-mb-2 lp-border-0 lp-pb-0">
                    <BellRing size={28} color="var(--lp-accent)" />
                    <span>Bildirimleri Açın</span>
                  </div>
                  <p className="notification-desc">
                    Size özel indirimler, randevu hatırlatmaları ve boş saat
                    bildirimleri için izin verin.
                  </p>
                  <button
                    type="button"
                    className="btn-enable-notify"
                    onClick={handleEnableNotifications}
                  >
                    BİLDİRİMLERE İZİN VER
                  </button>
                </div>

                <h6 className="section-divider-title">UYGULAMAYI YÜKLE</h6>

                <div className="install-tabs">
                  <div className="install-card ios-card">
                    <div className="os-header">
                      <Smartphone size={26} />
                      <span>iPhone / iOS</span>
                    </div>
                    <div className="steps-list">
                      <div className="step-item">
                        <span className="step-number">
                          <Share size={14} />
                        </span>
                        <span className="step-text">
                          Safari alt menüsündeki <strong>"Paylaş"</strong>{" "}
                          butonuna basın.
                        </span>
                      </div>
                      <div className="step-item">
                        <span className="step-number">
                          <PlusSquare size={14} />
                        </span>
                        <span className="step-text">
                          Açılan menüde <strong>"Ana Ekrana Ekle"</strong>{" "}
                          seçeneğine dokunun.
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="install-card android-card lp-mt-4">
                    <div className="os-header">
                      <Smartphone size={26} />
                      <span>Android</span>
                    </div>
                    <div className="steps-list">
                      <div className="step-item">
                        <span className="step-number">1</span>
                        <span className="step-text">
                          Tarayıcı menüsünü (3 nokta) açın.
                        </span>
                      </div>
                      <div className="step-item">
                        <span className="step-number">2</span>
                        <span className="step-text">
                          <strong>"Uygulamayı Yükle"</strong> veya{" "}
                          <strong>"Ana Ekrana Ekle"</strong> seçeneğine
                          tıklayın.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
